import { motion } from 'framer-motion';
import Button from '@components/common/Button';

/**
 * HeroBanner Component
 * Props:
 * - imageUrl
 * - title
 * - subtitle
 * - primaryCTA {text, onClick}
 * - secondaryCTA {text, onClick}
 * Features:
 * - Full-width responsive image
 * - Optional carousel for multiple banners
 * - Animations: fade-in text, scale button hover, text slide-in
 * Loose Coupling: actions via props
 */

const HeroBanner = ({
  imageUrl = '/hero-banner.jpg',
  title = 'Secure Your World',
  subtitle = 'Professional Security Solutions for Home and Business',
  primaryCTA = { text: 'Shop Now', onClick: () => {} },
  secondaryCTA = { text: 'Learn More', onClick: () => {} },
}) => {
  return (
    <div className="relative h-[550px] md:h-[650px] overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/85 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative container-custom h-full flex items-center">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4"
          >
            <span className="text-accent-500 uppercase text-sm tracking-widest font-semibold">
              Since 2003
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            {title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-2xl text-gray-100 mb-10 font-light"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              text={primaryCTA.text}
              type="primary"
              onClick={primaryCTA.onClick}
            />
            {secondaryCTA && (
              <Button
                text={secondaryCTA.text}
                type="secondary"
                onClick={secondaryCTA.onClick}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-800"
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;

